import PropTypes from 'prop-types';
import InfoDialog from '../InfoDialog';

const LearnMoreModal = ({ learnMoreType, openLearnMore, closeLearnMore }) => {
  const getLearnMoreModalHeading = type => {
    switch (type) {
      case 's3upload':
      case 'azureUpload':
      case 'gcsUpload':
      case 'mftUpload':
        return 'Ingestion File Source Details';
      case 'auditfields':
        return 'Audit Fields';
      case 'basicConf':
        return 'Ingestion File Setup';
      case 'basicConfRedshift':
      case 'basicConfSalesforce':
        return 'Ingestion Table Setup';
      case 'dataShare':
      case 's3bucketName':
      case 'gcsbucketName':
      case 'azurebucketName':
      case 'mftMailbox':
      case 'salesforce':
        return 'Source Configured';
      case 'genfields':
        return 'Generated Fields';
      case 'dedupe':
        return 'Deduplication Key & Order Handling';
      case 's3Connector':
        return 'S3 Connector';
      case 'azureConnector':
        return 'Azure Blob Storage Connector';
      case 'redshiftConnector':
        return 'Redshift Connector';
      case 'bigqueryConnector':
        return 'BigQuery Connector';
      case 'gcsConnector':
        return 'GCS Connector';
      case 'posthogConnector':
        return 'Posthog Connector';
      case 'salesforceConnector':
        return 'Salesforce Connector';
      case 'googleAnalyticsConnector':
        return 'Google Analytics Connector';
      case 'MFU':
        return 'Direct File Upload';

      default:
        return '';
    }
  };

  const getLearnMoreModalContent = type => {
    switch (type) {
      case 'auditfields':
        return (
          <span>
            Select the date/timestamp column used to fetch only records
            modified/inserted in the Redshift table. E.g: CREATED_DT/UPDATED_DT
          </span>
        );
      case 'azureUpload':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Search File Name</strong>: Search and select the file by
                name and path.
              </li>
              <li>
                {' '}
                <strong>Selected Folder Structure</strong>: Auto populated.
                Displays the folder structure within the chosen Azure Blob
                Storage container.
              </li>
              <li>
                {' '}
                <strong>Selected File</strong>: Auto populated. Displays the
                selected ingestion file.
              </li>
              <li>
                {' '}
                <strong>Delimiter</strong>: Present for delimited files,
                character used to separate data.
              </li>
              <li>
                {' '}
                <strong>Escape</strong>: Escape characters treat delimiters as
                data, avoiding misinterpretation. E.g., Using double quotes as
                an escape character ("Value1,Value2") treats the comma as data,
                not a delimiter.
              </li>
              <li>
                {' '}
                <strong>Use metadata file</strong> (optional): Check this box to
                select a metadata file from the same container as the sample.
                Use <strong>Download Template</strong> for the required columns
                and accepted values. Metadata defines types, lengths, date
                formats, nullability, and encryption; column names must match
                the sample file exactly (case-sensitive).
              </li>
              <li>
                {' '}
                <strong>Process File</strong>: Processes the sample (and
                metadata, if selected). Without metadata, schema is inferred
                from the sample. With metadata, Process fails if columns are
                missing, extra, or do not match exactly.
              </li>
            </ol>
          </>
        );
      case 's3upload':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Search File Name</strong>: Search and select the file by
                name and path.
              </li>
              <li>
                {' '}
                <strong>Selected Folder Structure</strong>: Auto populated.
                Displays the folder structure within the chosen S3 bucket.
              </li>
              <li>
                {' '}
                <strong>Selected File</strong>: Auto populated. Displays the
                selected ingestion file.
              </li>
              <li>
                {' '}
                <strong>Delimiter</strong>: Present for delimited files,
                character used to separate data.
              </li>
              <li>
                {' '}
                <strong>Escape</strong>: Escape characters treat delimiters as
                data, avoiding misinterpretation. E.g., Using double quotes as
                an escape character ("Value1,Value2") treats the comma as data,
                not a delimiter.
              </li>
              <li>
                {' '}
                <strong>Use metadata file</strong> (optional): Check this box to
                select a metadata file from the same bucket as the sample. Use{' '}
                <strong>Download Template</strong> for the required columns and
                accepted values. Metadata defines types, lengths, date formats,
                nullability, and encryption; column names must match the sample
                file exactly (case-sensitive).
              </li>
              <li>
                {' '}
                <strong>Process File</strong>: Processes the sample (and
                metadata, if selected). Without metadata, schema is inferred
                from the sample. With metadata, Process fails if columns are
                missing, extra, or do not match exactly.
              </li>
            </ol>
          </>
        );
      case 'mftUpload':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Search File Name</strong>: Search and select a sample
                file from the selected MFT mailbox. File name format:{' '}
                <code>DAX_&lt;feedname&gt;_yyyymmdd_hhmmss.extension</code> (for
                example, <code>DAX_HIGHDATA011_20260731_063747.txt</code>).
              </li>
              <li>
                {' '}
                <strong>Selected Folder Structure</strong>: Auto populated.
                Displays the path within the mailbox for the selected file.
              </li>
              <li>
                {' '}
                <strong>Selected File</strong>: Auto populated. Displays the
                selected ingestion file.
              </li>
              <li>
                {' '}
                <strong>Delimiter</strong>: Present for delimited files,
                character used to separate data.
              </li>
              <li>
                {' '}
                <strong>Escape</strong>: Escape characters treat delimiters as
                data, avoiding misinterpretation. E.g., Using double quotes as
                an escape character ("Value1,Value2") treats the comma as data,
                not a delimiter.
              </li>
              <li>
                {' '}
                <strong>Use metadata file</strong> (optional): Check this box to
                select a metadata file from the same mailbox as the sample. Use{' '}
                <strong>Download Template</strong> for the required columns and
                accepted values. Metadata defines types, lengths, date formats,
                nullability, and encryption; column names must match the sample
                file exactly (case-sensitive).
              </li>
              <li>
                {' '}
                <strong>Process File</strong>: Processes the sample (and
                metadata, if selected). Without metadata, schema is inferred
                from the sample. With metadata, Process fails if columns are
                missing, extra, or do not match exactly.
              </li>
            </ol>
          </>
        );
      case 'gcsUpload':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Search File Name</strong>: Search and select the file by
                name and path.
              </li>
              <li>
                {' '}
                <strong>Selected Folder Structure</strong>: Auto populated.
                Displays the folder structure within the chosen GCS bucket.
              </li>
              <li>
                {' '}
                <strong>Selected File</strong>: Auto populated. Displays the
                selected ingestion file.
              </li>
              <li>
                {' '}
                <strong>Delimiter</strong>: Present for delimited files,
                character used to separate data.
              </li>
              <li>
                {' '}
                <strong>Escape</strong>: Escape characters treat delimiters as
                data, avoiding misinterpretation. E.g., Using double quotes as
                an escape character ("Value1,Value2") treats the comma as data,
                not a delimiter.
              </li>
              <li>
                {' '}
                <strong>Use metadata file</strong> (optional): Check this box to
                select a metadata file from the same bucket as the sample. Use{' '}
                <strong>Download Template</strong> for the required columns and
                accepted values. Metadata defines types, lengths, date formats,
                nullability, and encryption; column names must match the sample
                file exactly (case-sensitive).
              </li>
              <li>
                {' '}
                <strong>Process File</strong>: Processes the sample (and
                metadata, if selected). Without metadata, schema is inferred
                from the sample. With metadata, Process fails if columns are
                missing, extra, or do not match exactly.
              </li>
            </ol>
          </>
        );
      case 'basicConfRedshift':
      case 'basicConfSalesforce':
      case 'basicConf':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Add Description For Your Feed *</strong>: Text to help
                identify the file being ingested.
              </li>
              <li>
                {' '}
                <strong>Data Category *</strong>: This depends on the file's
                data and defines the processing needs.
              </li>
              <li>
                {' '}
                <strong>Priority Processing</strong>: Selecting the checkbox
                enables rapid ingestion for specific Data Categories and Data
                Volume ranges, otherwise, the feed follows scheduled ingestion.
              </li>
              <li>
                {' '}
                <strong>Content Type *</strong>: Incremental Data: Adds or
                updates only changes. ; Full Replacement: Replaces all data
                entirely.
              </li>
              <li>
                {' '}
                <strong>Volume Of Data Expected *</strong>: Buckets are created
                based on the expected data amount.
              </li>
              <li>
                {' '}
                <strong>Feed Priority Ranking</strong>: Resolves data conflicts
                by prioritizing feeds (lower numbers = higher priority)
              </li>
              <li>
                {' '}
                <strong>Reject Threshold Percentage *</strong>: Maximum allowed
                percentage of invalid records before the file is rejected.
              </li>
            </ol>
          </>
        );
      case 'dataShare':
        return (
          <span>
            A Redshift <strong>data share</strong> enables secure, real-time
            data sharing. Selected share contains the ingestion table. Select{' '}
            <strong>object</strong> (schema, table, or view) that represents the
            ingestion table.
          </span>
        );
      case 'genfields':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Field Name</strong>: Enter a name for the new field.
              </li>
              <li>
                {' '}
                <strong>Data Type</strong>: Choose the data type.
              </li>
              <li>
                {' '}
                <strong>Default Value</strong>: Set a default value (e.g. 0) if
                no data is available.
              </li>
              <li>
                {' '}
                <strong>Length</strong>: Specify the field's maximum length
                (e.g., 255 characters).
              </li>
              <li>
                {' '}
                <strong>Value Generation Logic</strong>: Enter your data
                transformation logic.
              </li>
              <li>
                {' '}
                <strong>Edit icon</strong>: Use the dropdown to select columns
                or functions, then click the '+' icon to add them to the RESULT
                field while building logic for a new field.
              </li>
            </ol>
          </>
        );
      case 'dedupe':
        return (
          <>
            <ol>
              <li>
                {' '}
                <strong>Select Deduplication Field(s)</strong>: Choose the
                field(s) to be used for identifying and removing duplicates{' '}
                <i>
                  At least one field must be selected when the Content Type is
                  set to <strong>'Full'</strong> for{' '}
                  <strong>
                    {' '}
                    Customer Data, Prospect Data, Suppression Data, Transaction
                    Data,
                  </strong>{' '}
                  and <strong>3rd Party Data Enrichment.</strong>
                </i>
              </li>
              <li>
                {' '}
                <strong>Select Order Handling Field(s)</strong>: Select field(s)
                for deduplication and define their processing order.
              </li>
              <li>
                {' '}
                <strong>Set Order</strong>: Rank duplicates using selected
                fields and specify ASC/DESC. For e.g., choose DESC for the most
                recent entry, like the latest date.
              </li>
            </ol>
          </>
        );
      case 's3bucketName':
        return (
          <span>
            An S3 bucket is a storage container in AWS for files, providing
            scalable and secure cloud storage. The{' '}
            <strong>selected bucket</strong> contains the ingestion file.
          </span>
        );
      case 'azurebucketName':
        return (
          <span>
            An Azure Blob Storage is a storage container in Azure used to store
            and manage data. The <strong>selected container</strong> contains
            the ingestion file.
          </span>
        );
      case 'gcsbucketName':
        return (
          <span>
            A GCS bucket is a storage container in GCP for files, providing
            scalable and secure cloud storage. The{' '}
            <strong>selected bucket</strong> contains the ingestion file.
          </span>
        );
      case 'mftMailbox':
        return (
          <span>
            An MFT mailbox is where Managed File Transfer deposits inbound files
            for this connector. The <strong>selected mailbox</strong> is used to
            search and process the sample ingestion file.
          </span>
        );
      case 'salesforce':
        return (
          <span>
            Salesforce is a customer relationship management (CRM) platform. The{' '}
            <strong>selected Client ID</strong> identifies your Salesforce org.
            Select the <strong>object</strong> that represents the ingestion
            data source.
          </span>
        );
      case 's3Connector':
        return (
          <>
            <span>
              An S3 bucket is a storage container in AWS used to store and
              manage data. Select the <strong>Bucket Region</strong>,{' '}
              <strong>Bucket Name</strong> and <strong>Connector Name</strong>{' '}
              in the fields provided. Once all fields are filled, the{' '}
              <strong>Test Connection</strong> button will become clickable.
              Click <strong>Test Connection</strong>, and if successful, proceed
              to <strong>Create Connector</strong>.
            </span>
          </>
        );
      case 'azureConnector':
        return (
          <>
            <span>
              Azure Blob Storage is a storage container in Azure used to store
              and manage data. Enter the <strong>Storage Account</strong>,{' '}
              <strong>Connector Name</strong>, and <strong>Container</strong> in
              the fields provided. Once all fields are filled, the{' '}
              <strong>Test Connection</strong> button will become clickable.
              Click <strong>Test Connection</strong>, and if successful, proceed
              to <strong>Create Connector</strong>.
            </span>
          </>
        );
      case 'gcsConnector':
        return (
          <>
            <span>
              A GCS bucket is a storage container in GCP used to store and
              manage data. Select the <strong>Bucket Region</strong>,{' '}
              <strong>Bucket Name</strong> and <strong>Connector Name</strong>{' '}
              in the fields provided. Once all fields are filled, the{' '}
              <strong>Test Connection</strong> button will become clickable.
              Click <strong>Test Connection</strong>, and if successful, proceed
              to <strong>Create Connector</strong>.
            </span>
          </>
        );
      case 'redshiftConnector':
        return (
          <span>
            In Redshift, a data share securely shares live, read-only data
            across clusters or accounts. Enter the{' '}
            <strong>DataShare name</strong> and <strong>Connector Name</strong>{' '}
            in the fields provided. Once both fields are filled, the{' '}
            <strong>Test Connection</strong> button will become clickable. Click{' '}
            <strong>Test Connection</strong>, and if successful, proceed to{' '}
            <strong>Create Connector</strong>.
          </span>
        );

      case 'bigqueryConnector':
        return (
          <span>
            BigQuery is Google Cloud's fully managed data warehouse for
            large-scale analytics. Enter the <strong>Dataset ID</strong> and{' '}
            <strong>Connector Name</strong> in the fields provided. Once both
            fields are filled, the <strong>Test Connection</strong> button will
            become clickable. Click <strong>Test Connection</strong>, and if
            successful, proceed to <strong>Create Connector</strong>.
          </span>
        );

      case 'posthogConnector':
        return (
          <span>
            PostHog is a product analytics platform for tracking events, user
            behavior, and feature usage. Enter the <strong>Project ID</strong>{' '}
            and <strong> Connector Name</strong> in the fields provided. Once
            both fields are filled, the <strong>Test Connection</strong> button
            will become clickable. Click <strong>Test Connection</strong>, and
            if successful, proceed to <strong>Create Connector</strong>.
          </span>
        );
      case 'salesforceConnector':
        return (
          <span>
            Salesforce is a customer relationship management (CRM) platform used
            for managing customer data, sales processes, and integrations. Enter
            the <strong>Client ID</strong> and <strong>Connector Name</strong>{' '}
            in the fields provided. Once both fields are filled, the{' '}
            <strong>Test Connection</strong> button will become clickable. Click{' '}
            <strong>Test Connection</strong>, and if successful, proceed to{' '}
            <strong>Create Connector</strong>.
          </span>
        );
      case 'googleAnalyticsConnector':
        return (
          <span>
            Google Analytics is a web analytics service for tracking and
            reporting website traffic and user interactions. Enter the{' '}
            <strong>Property ID</strong> and <strong>Connector Name</strong> in
            the fields provided. Once both fields are filled, the{' '}
            <strong>Test Connection</strong> button will become clickable. Click{' '}
            <strong>Test Connection</strong>, and if successful, proceed to{' '}
            <strong>Create Connector</strong>.
          </span>
        );
      case 'MFU':
        return (
          <>
            <ol>
              <li>
                <strong>File naming:</strong> The file must follow{' '}
                <strong>
                  DAX_&lt;feedname&gt;_YYYYMMDD_HHMMSS.&lt;ext&gt;
                </strong>{' '}
                (example: <strong>DAX_CUSTOMERS_20260123_143052.csv</strong>).
                This is used for validation and to infer the feed name.
              </li>
              <li>
                <strong>Supported formats:</strong> <strong>.csv</strong>,{' '}
                <strong>.tsv</strong>, <strong>.txt</strong>,{' '}
                <strong>.xlsx</strong>, <strong>.xls</strong>.
              </li>
              <li>
                <strong>Delimiter / Escape (CSV/TSV/TXT only):</strong> Select
                the correct <strong>Delimiter</strong> (comma, pipe, tab, etc.)
                and <strong>Escape</strong> (usually <code>"</code>) so quoted
                text is parsed correctly.
              </li>
              <li>
                <strong>Upload &amp; Process:</strong> Click{' '}
                <strong>Upload &amp; Process</strong> (or{' '}
                <strong>Re-upload &amp; Process</strong> when editing). The
                progress bar shows upload status, then the file is processed and
                the schema is detected.
              </li>
              <li>
                <strong>After processing:</strong> Complete the required
                ingestion setup fields (Description, Data Category, Content
                Type, Reject Threshold, etc.) before proceeding.
              </li>
            </ol>
          </>
        );
      default:
        return '';
    }
  };

  return (
    <InfoDialog
      openDialog={openLearnMore}
      setOpenDialog={() => closeLearnMore()}
      type="info"
      messageInfo={{
        message: getLearnMoreModalHeading(learnMoreType),
        detail: getLearnMoreModalContent(learnMoreType),
      }}
    />
  );
};

LearnMoreModal.propTypes = {
  learnMoreType: PropTypes.string,
  openLearnMore: PropTypes.bool.isRequired,
  closeLearnMore: PropTypes.func.isRequired,
};

export default LearnMoreModal;
